import React, { useMemo } from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'
import type { DSIcon } from '@/design-system'
import type { DSColor } from '@ppwr/poker'
import { monthDayAndTimeFormatter } from '../../../utils/dateFormatter'

const CARD_WIDTH = 204
const CARD_HEIGHT = 240
const PATTERN_HEIGHT = 16

type GameMode =
  | 'practice'
  | 'tournament'
  | 'cash'
  | 'daily10'
  | 'quick'
  | 'communityGame'
  | string

const MODE_LABELS: Record<string, string> = {
  tournament: 'Tournament',
  cash: 'Cash Game',
  daily10: 'Daily 10',
  quick: 'Quick Game',
  communityGame: 'Community',
  practice: 'Practice'
}

const formatModeLabel = (mode: GameMode | undefined) => {
  if (!mode) return 'Game'
  return MODE_LABELS[mode] ?? 'Game'
}

export type SchedulePhase = 'starting_soon' | 'join_window' | 'in_progress'

export type LoungeScheduleCardProps = {
  name: string
  mode?: GameMode
  smallBlind?: number
  bigBlind?: number
  maxPlayers: number
  playerCount: number
  state: 'pregame' | 'playing' | 'finished' | 'deleted'
  pregameStartAt?: number
  startedAt?: number
  ctaLabel: string
  onPress: () => void
  disabled?: boolean
  patternSuit?: 'club' | 'heart'
  schedulePhase?: SchedulePhase
  /** Visually de-emphasize the card (e.g. next week's scheduled games). */
  dimmed?: boolean
  /** Hide the call-to-action button (e.g. informational, non-joinable scheduled occurrences). */
  hideCta?: boolean
}

const PATTERN_KEYS = Array.from({ length: 12 }, (_, i) => `pattern-${i}`)

function SuitPatternRow({ suit }: { suit: 'club' | 'heart' }) {
  const iconName: DSIcon = suit === 'club' ? 'Club' : 'Suite--Heart'
  const stripeColor = suit === 'club' ? DS.palette['Brand/Purple'] : DS.palette['Brand/Maroon']
  const iconColor: DSColor = suit === 'club' ? 'Brand/Orange' : 'Brand/Pink'

  return (
    <View style={[styles.patternRow, { backgroundColor: stripeColor }]}>
      {PATTERN_KEYS.map((key) => (
        <View key={key} style={styles.patternIcon}>
          <DS.Icon name={iconName} size={11} color={iconColor} />
        </View>
      ))}
    </View>
  )
}

export function LoungeScheduleCard({
  name,
  mode,
  smallBlind,
  bigBlind,
  maxPlayers,
  playerCount,
  state,
  pregameStartAt,
  ctaLabel,
  onPress,
  disabled,
  patternSuit,
  schedulePhase,
  dimmed,
  hideCta
}: LoungeScheduleCardProps) {
  const isLive = state === 'playing'
  const seatsOpen = Math.max(maxPlayers - playerCount, 0)

  const dateLabel = useMemo(() => {
    if (isLive || !pregameStartAt) return null
    return monthDayAndTimeFormatter(new Date(pregameStartAt))
  }, [isLive, pregameStartAt])

  const blindsLabel =
    bigBlind && smallBlind ? `Blinds ${smallBlind}/${bigBlind}` : bigBlind ? `Blinds ${bigBlind}` : null

  const effectiveSuit: 'club' | 'heart' = patternSuit ?? (isLive ? 'club' : 'heart')

  return (
    <View style={[styles.card, dimmed && styles.cardDimmed]}>
      <View style={styles.body}>
        <DS.Text variant="Body Small/Regular" style={{ color: DS.palette['Global/Black'] }}>
          {formatModeLabel(mode)}
        </DS.Text>
        <View style={styles.titleBlock}>
          <DS.Text variant="Heading 5/Medium" numberOfLines={1}>
            {name}
          </DS.Text>
          {blindsLabel && (
            <DS.Text variant="Body Small/Regular" style={{ color: DS.palette['Global/Black'] }}>
              {blindsLabel}
            </DS.Text>
          )}
          <View style={styles.seatsRow}>
            <DS.Icon name="Person" size={14} color="Brand/Orange" />
            <DS.Text variant="Body Small/Regular" numberOfLines={1} style={{ color: DS.palette['Global/Black'] }}>
              {playerCount}/{maxPlayers} • {seatsOpen} {seatsOpen === 1 ? 'seat' : 'seats'} open
            </DS.Text>
          </View>
          {dateLabel && (
            <DS.Text
              variant="Caption/Bold"
              style={{ color: DS.palette['Brand/Dark Grey'], paddingTop: 4 }}
              numberOfLines={1}
            >
              {dateLabel}
            </DS.Text>
          )}
        </View>
      </View>

      {!hideCta && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`${ctaLabel} ${name}`}
          onPress={onPress}
          disabled={disabled}
          style={({ pressed }) => [
            styles.cta,
            disabled && styles.ctaDisabled,
            pressed && !disabled && { opacity: 0.85 }
          ]}
        >
          <DS.Text variant="Body Small/Medium" style={{ color: '#fff' }}>
            {ctaLabel}
          </DS.Text>
        </Pressable>
      )}

      {isLive && (
        <View style={styles.liveBadge}>
          <DS.Text variant="Caption/Bold" style={{ color: '#fff' }}>
            LIVE
          </DS.Text>
        </View>
      )}
      {!isLive && schedulePhase === 'starting_soon' && (
        <View style={styles.soonBadge}>
          <DS.Text variant="Caption/Bold" style={{ color: '#fff' }}>
            SOON
          </DS.Text>
        </View>
      )}
      {!isLive && schedulePhase === 'join_window' && (
        <View style={styles.joinBadge}>
          <DS.Text variant="Caption/Bold" style={{ color: '#fff' }}>
            JOIN NOW
          </DS.Text>
        </View>
      )}
      {!isLive && schedulePhase === 'in_progress' && (
        <View style={styles.liveBadge}>
          <DS.Text variant="Caption/Bold" style={{ color: '#fff' }}>
            LIVE
          </DS.Text>
        </View>
      )}

      <View style={styles.patternContainer}>
        <SuitPatternRow suit={effectiveSuit} />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 32,
    justifyContent: 'space-between',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2
  },
  cardDimmed: {
    opacity: 0.5,
    backgroundColor: DS.palette['Global/White']
  },
  body: {
    gap: 8
  },
  titleBlock: {
    gap: 4
  },
  seatsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6
  },
  cta: {
    backgroundColor: DS.palette['Global/Black'],
    borderRadius: 32,
    paddingHorizontal: 24,
    paddingVertical: 10.5,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  ctaDisabled: {
    backgroundColor: DS.palette['Brand/Dark Grey'],
    opacity: 0.6
  },
  liveBadge: {
    position: 'absolute',
    top: 0,
    right: 16,
    backgroundColor: DS.palette['Brand/Orange'],
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 44,
    alignItems: 'center'
  },
  soonBadge: {
    position: 'absolute',
    top: 0,
    right: 16,
    backgroundColor: DS.palette['Brand/Purple'],
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 44,
    alignItems: 'center'
  },
  joinBadge: {
    position: 'absolute',
    top: 0,
    right: 16,
    backgroundColor: DS.palette['Brand/Maroon'],
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 60,
    alignItems: 'center'
  },
  patternContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: PATTERN_HEIGHT,
    overflow: 'hidden'
  },
  patternRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: 4
  },
  patternIcon: {
    alignItems: 'center',
    justifyContent: 'center'
  }
})
